import type { ApiCollection, ApiErrorBody, ApiResponse, ApiSuccess } from './types';

/**
 * Typed API client for the NeuroEdge Connect backend.
 *
 * - All calls target the versioned base URL (`VITE_API_BASE_URL`, e.g.
 *   http://localhost:3000/api/v1).
 * - Sends and reads the `X-Correlation-Id` header.
 * - Unwraps the standard success envelope; throws `ApiClientError` carrying the
 *   stable error code on any failure so callers/React Query can branch on it.
 * - Never stores secrets or provider tokens. Auth tokens (added later) belong in
 *   memory or secure cookies, not localStorage.
 */

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/api/v1';
const CORRELATION_ID_HEADER = 'x-correlation-id';
/** For the few callers that must bypass `apiClient` (e.g. the error reporter). */
export const API_BASE_URL = BASE_URL;

/**
 * Auth wiring. The AuthProvider keeps these in sync:
 * - `setAccessToken` — the in-memory bearer token, attached to every
 *   non-`/auth/*` request. Set synchronously on login / refresh, cleared on
 *   logout.
 * - `setTokenRefresher` — a callback that performs a silent refresh; a request
 *   that fails with `401 AUTH_SESSION_EXPIRED` is retried once if it resolves
 *   `true`. The refresh token itself only lives in the httpOnly cookie.
 */
let accessToken: string | null = null;
let tokenRefresher: (() => Promise<boolean>) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}
export function setTokenRefresher(fn: (() => Promise<boolean>) | null): void {
  tokenRefresher = fn;
}

// Only these three genuinely don't need (or can't use) the bearer token:
// register/login are unauthenticated by definition, and refresh reads the
// refresh token from the body/httpOnly cookie, not an access token. Every
// other `/auth/*` path - notably `/auth/me` and `/auth/logout` - IS gated by
// `app.authenticate` on the backend and needs the header like any other
// protected route. A blanket `path.startsWith('/auth/')` here previously
// stripped the Authorization header off `/auth/me`/`/auth/logout` too,
// which made every session-resume `/auth/me` call fail with 401 regardless
// of how valid the just-refreshed access token was.
const NO_AUTH_HEADER_PATHS = new Set(['/auth/register', '/auth/login', '/auth/refresh']);
const isAuthPath = (path: string) => NO_AUTH_HEADER_PATHS.has(path);

export class ApiClientError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly details?: Record<string, unknown>;
  readonly correlationId?: string;

  constructor(body: ApiErrorBody, httpStatus: number, correlationId?: string) {
    super(body.message);
    this.name = 'ApiClientError';
    this.code = body.code;
    this.httpStatus = httpStatus;
    this.details = body.details;
    this.correlationId = correlationId;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  /** Optional client-generated correlation id; the server generates one otherwise. */
  correlationId?: string;
  /** Extra headers (e.g. `Authorization`). Merged over the defaults. */
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * Single request core: builds headers (incl. the bearer token for non-`/auth/*`
 * paths), fetches, parses the standard envelope, and performs one silent-refresh
 * retry on `401 AUTH_SESSION_EXPIRED`. Returns the raw `ApiResponse` so both the
 * data-unwrapping and full-envelope helpers can share it.
 */
async function send<T>(
  path: string,
  options: RequestOptions = {},
  retrying = false,
): Promise<ApiResponse<T>> {
  const { method = 'GET', body, query, signal, correlationId } = options;

  // `FormData` (the media-upload route) must not be JSON-stringified, and
  // its `Content-Type` (multipart, with a boundary) must come from `fetch`
  // itself - setting it here would omit the boundary and break parsing.
  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';
  if (correlationId) headers[CORRELATION_ID_HEADER] = correlationId;
  if (accessToken && !isAuthPath(path)) headers.Authorization = `Bearer ${accessToken}`;
  if (options.headers) Object.assign(headers, options.headers);

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
      credentials: 'include',
      signal,
    });
  } catch {
    throw new ApiClientError(
      { code: 'INTERNAL_ERROR', message: 'Network request failed.' },
      0,
      undefined,
    );
  }

  const responseCorrelationId = res.headers.get(CORRELATION_ID_HEADER) ?? undefined;

  if (res.status === 204) {
    return {
      success: true,
      data: undefined as T,
      meta: {},
      correlationId: responseCorrelationId ?? '',
    };
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError(
      { code: 'INTERNAL_ERROR', message: 'Malformed response from the server.' },
      res.status,
      responseCorrelationId,
    );
  }

  if (!res.ok || payload.success === false) {
    const errorBody: ApiErrorBody =
      payload.success === false
        ? payload.error
        : { code: 'INTERNAL_ERROR', message: 'Request failed.' };

    // One transparent retry after a silent token refresh.
    if (
      res.status === 401 &&
      !retrying &&
      !isAuthPath(path) &&
      tokenRefresher &&
      errorBody.code === 'AUTH_SESSION_EXPIRED'
    ) {
      const refreshed = await tokenRefresher();
      if (refreshed) return send<T>(path, options, true);
    }

    throw new ApiClientError(errorBody, res.status, responseCorrelationId ?? payload.correlationId);
  }

  return payload;
}

export interface BlobResponse {
  blob: Blob;
  fileName: string | null;
}

function fileNameFromContentDisposition(value: string | null): string | null {
  if (!value) return null;
  const match = /filename="?([^"; ][^";]*)"?/i.exec(value);
  return match?.[1] ?? null;
}

/**
 * Fetches a binary response (e.g. an on-demand media download) - same
 * auth-header/silent-refresh-retry handling as the JSON methods above, but
 * the success body is never JSON here, so it's returned as a `Blob` instead
 * of being parsed/unwrapped through `send()`.
 */
async function fetchBlob(
  path: string,
  options: Omit<RequestOptions, 'method' | 'body'> = {},
  retrying = false,
): Promise<BlobResponse> {
  const headers: Record<string, string> = {};
  if (options.correlationId) headers[CORRELATION_ID_HEADER] = options.correlationId;
  if (accessToken && !isAuthPath(path)) headers.Authorization = `Bearer ${accessToken}`;
  if (options.headers) Object.assign(headers, options.headers);

  let res: Response;
  try {
    res = await fetch(buildUrl(path, options.query), {
      method: 'GET',
      headers,
      credentials: 'include',
      signal: options.signal,
    });
  } catch {
    throw new ApiClientError(
      { code: 'INTERNAL_ERROR', message: 'Network request failed.' },
      0,
      undefined,
    );
  }

  const responseCorrelationId = res.headers.get(CORRELATION_ID_HEADER) ?? undefined;

  if (!res.ok) {
    let errorBody: ApiErrorBody = { code: 'INTERNAL_ERROR', message: 'Request failed.' };
    try {
      const payload = (await res.json()) as ApiResponse<unknown>;
      if (payload.success === false) errorBody = payload.error;
    } catch {
      // Non-JSON error body - keep the generic message.
    }

    if (
      res.status === 401 &&
      !retrying &&
      tokenRefresher &&
      errorBody.code === 'AUTH_SESSION_EXPIRED'
    ) {
      const refreshed = await tokenRefresher();
      if (refreshed) return fetchBlob(path, options, true);
    }

    throw new ApiClientError(errorBody, res.status, responseCorrelationId);
  }

  const blob = await res.blob();
  const fileName = fileNameFromContentDisposition(res.headers.get('content-disposition'));
  return { blob, fileName };
}

/** Unwraps the `data` of a single-resource response. */
async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const payload = await send<T>(path, options);
  return (payload as ApiSuccess<T> | ApiCollection<T>).data as T;
}

/** Returns the full envelope (data + pagination meta) - use for collections. */
async function requestEnvelope<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiCollection<T>> {
  return (await send<T>(path, options)) as ApiCollection<T>;
}

export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  getCollection: requestEnvelope,
  getBlob: fetchBlob,
};
